import DOMPurify from 'dompurify';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { getTooltipProps, TooltipConfig } from 'packages/radiant-react/typography/typography-utils';

export interface Props {
  /**
   * HTML string e.g. `<div>Hello world</div>`
   */
  html: string;
  /**
   * Optional class name for the element
   */
  className?: string;
  /**
   * Optional DOMPurify config for the element
   */
  config?: DOMPurify.Config;

  /**
   * To show Tooltip on Typography component.
   * Typgraphy by default will show tooltip when the text is truncated using ellipsis prop.
   * However, if the user wants to override any behavior then attributes of tooltipConfig prop will be given priority.
   *
   * dataTooltipContent: string; --> Text for the Tooltip
   *
   * dataTooltipAlign?: string; --> Alignment of tooltip
   *
   * dataTooltipPosition?: string; --> Positon of tooltip
   *
   * showTooltipOnlyOnEllipsis?: boolean; @default false --> Show tooltip only when ellipsis are added or else won't show tooltip. When set to false, tooltip will be always shown on hover
   *
   * disableTooltip?: boolean; @default false --> To disable the tooltip
   */
  tooltipConfig?: TooltipConfig;
  /**
   * Optional styles for the element.
   */
  style?: CSSProperties;
}

/**
 * SafeHTML is a thin wrapper over React's dangerouslySetInnerHTML.
 * It sanitizes the input HTML string using dompurify's sanitize function.
 *
 * Example:
 *
 * - Use this:
 *
 * ```
 * <SafeHTML html={myHTMLString} />
 * ```
 *
 * - Don't use this:
 *
 * ```
 * <div dangerouslySetInnerHTML={{ __html: myHTMLString }} />
 * ```
 */
export const SafeHTML: React.FC<Props> = ({
  html,
  className,
  config,
  tooltipConfig,
  ...restProps
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isEllipsisShowing, setIsEllipsisShowing] = useState(false);

  const { tooltipProps, sanitizedHTMLString } = React.useMemo(() => {
    let sanitizedHTML: string;
    if (config) {
      sanitizedHTML = DOMPurify.sanitize(html, {
        ...config,
        RETURN_DOM_FRAGMENT: false,
        RETURN_DOM: false,
      });
    } else {
      sanitizedHTML = DOMPurify.sanitize(html);
    }
    return {
      tooltipProps: getTooltipProps(sanitizedHTML || html, tooltipConfig, isEllipsisShowing),
      sanitizedHTMLString: sanitizedHTML,
    };
  }, [isEllipsisShowing, config, tooltipConfig, html]);

  useEffect(() => {
    if (
      ref.current &&
      (ref.current.clientHeight < ref.current.scrollHeight ||
        ref.current.clientWidth < ref.current.scrollWidth)
    ) {
      setIsEllipsisShowing(true);
    } else {
      setIsEllipsisShowing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.current]);

  // When DOMPurify.sanitize(html) fails due to invalid html, it return empty string.
  // So, to handle that behaviour we need to return the value as a string itself
  return sanitizedHTMLString ? (
    <div
      className={className}
      ref={ref}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitizedHTMLString }}
      {...tooltipProps}
      {...restProps}
    />
  ) : (
    <div className={className} ref={ref} {...tooltipProps} {...restProps}>
      {html}
    </div>
  );
};

/**
 *
 * @param text text to check
 * @returns it returns boolean  on the basis of given test has HTML Tags present or not
 */
export const isHTML = (text: string): boolean => {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};
